import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Icon } from "@iconify/react";
import Common from "../../helper/common";
import { respEnum, OpenMatchStatusEnum, OpenMatchTypeEnum, RoleEnum } from "../../constants/appConstant";
import toast from "react-hot-toast";
import { Breadcrumbs, BreadcrumbItem } from "../../components/ui/breadcrumbs";
import { useQueryParams } from "../../contexts/queryParamsContext";
import moment from "moment";
import DialogModal from "../../components/partials/modal/dialogModal";
import FormikInput from "../../components/formik-components/formik-input";
import FormikSelect from "../../components/formik-components/formik-select";
import Swal from "sweetalert2";

const MatchDetails = () => {
  const navigate = useNavigate();
  const { params } = useQueryParams();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addPlayerModalOpen, setAddPlayerModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null); // { side, position }
  const [phoneCode, setPhoneCode] = useState("+92");
  const [phone, setPhone] = useState("");
  const [playerData, setPlayerData] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [phoneCodes, setPhoneCodes] = useState([]);
  const phoneInputRef = React.useRef(null);
  const [updatePlayerModalOpen, setUpdatePlayerModalOpen] = useState(false);
  const [selectedPlayerForUpdate, setSelectedPlayerForUpdate] = useState(null);
  const [updateSide, setUpdateSide] = useState("");
  const [updatePosition, setUpdatePosition] = useState("");
  const [resultUploadModalOpen, setResultUploadModalOpen] = useState(false);
  const [sideASets, setSideASets] = useState(["", "", ""]);
  const [sideBSets, setSideBSets] = useState(["", "", ""]);
  const [winner, setWinner] = useState(null);
  const matchId = params?.openMatchId;

  const getMatchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        `GetOpenMatchDetails?openMatchId=${matchId}`,
        null,
        "GET",
        false
      );

      if (response?.status === respEnum.Success) {
        setMatch(response.data);
      } else {
        toast.error(response?.message || "Failed to load match details");
      }
    } catch (error) {
      console.error("Error fetching match details:", error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    if (matchId) {
      getMatchDetails();
    }
    fetchPhoneCodes();
  }, [matchId, getMatchDetails]);

  // Focus phone input when modal opens
  useEffect(() => {
    if (addPlayerModalOpen && phoneInputRef.current) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        phoneInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [addPlayerModalOpen]);

  // Handle modal close
  const handleAddPlayerModalClose = useCallback((value) => {
    setAddPlayerModalOpen(value);
    if (!value) {
      setPlayerData(null);
      setPhone("");
      setSelectedSlot(null);
    }
  }, []);

  const fetchPhoneCodes = async () => {
    try {
      // Use hardcoded phone codes similar to createMatch.jsx
      const codes = [
        { value: "+92", label: "+92 Pakistan" },
        { value: "+1", label: "+1 USA/Canada" },
        { value: "+44", label: "+44 UK" },
        { value: "+971", label: "+971 UAE" },
        { value: "+966", label: "+966 Saudi Arabia" },
      ];
      setPhoneCodes(codes);
    } catch (error) {
      console.error("Error fetching phone codes:", error);
    }
  };

  const lookupUserByPhone = useCallback(async (phoneCode, phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 10) {
      return null;
    }

    setLookupLoading(true);
    try {
      const sportId = match?.sportId || 1; // Use match sportId or default to Padel
      const response = await Common.ApiService.getInstance().request(
        `CheckUserExistWithLevel?phone=${phoneNumber}&sportId=${sportId}`,
        null,
        "GET",
        false
      );

      if (response?.status === respEnum.Success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error looking up player:", error);
      return null;
    } finally {
      setLookupLoading(false);
    }
  }, [match?.sportId]);

  const handleAddPlayer = useCallback(async () => {
    if (!selectedSlot || !selectedSlot.side || !selectedSlot.position) {
      toast.error("Please select side and position first");
      return;
    }
    if (!playerData) {
      toast.error("Please search and verify player first");
      return;
    }

    try {
      const response = await Common.ApiService.getInstance().request(
        "AddPlayerToOpenMatchAdmin",
        {
          openMatchId: matchId,
          phoneCode: phoneCode,
          phone: phone,
          side: selectedSlot.side,
          position: selectedSlot.position
        },
        "POST",
        false
      );

      if (response?.status === respEnum.Success) {
        toast.success("Player added successfully!");
        setAddPlayerModalOpen(false);
        setPlayerData(null);
        setPhone("");
        setSelectedSlot(null);
        getMatchDetails();
      } else {
        toast.error(response?.message || "Failed to add player");
      }
    } catch (error) {
      console.error("Error adding player:", error);
      toast.error("An error occurred while adding player");
    }
  }, [selectedSlot, playerData, matchId, phoneCode, phone, getMatchDetails]);

  const handleRemovePlayer = async (playerId) => {
    const result = await Swal.fire({
      title: "Remove Player?",
      text: "Are you sure you want to remove this player from the match?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel"
    });

    if (result.isConfirmed) {
      try {
        const response = await Common.ApiService.getInstance().request(
          `RemovePlayerFromOpenMatchAdmin?openMatchId=${matchId}&playerId=${playerId}`,
          null,
          "DELETE",
          false
        );

        if (response?.status === respEnum.Success) {
          toast.success("Player removed successfully!");
          getMatchDetails();
        } else {
          toast.error(response?.message || "Failed to remove player");
        }
      } catch (error) {
        console.error("Error removing player:", error);
        toast.error("An error occurred while removing player");
      }
    }
  };

  const handleUpdatePlayer = (player) => {
    setSelectedPlayerForUpdate(player);
    setUpdateSide(player.side);
    setUpdatePosition(player.position);
    setUpdatePlayerModalOpen(true);
  };

  const handleUpdatePlayerSubmit = async () => {
    if (!selectedPlayerForUpdate || !updateSide || !updatePosition) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const response = await Common.ApiService.getInstance().request(
        "UpdatePlayerInOpenMatchAdmin",
        {
          openMatchId: matchId,
          playerId: selectedPlayerForUpdate.player_id,
          side: updateSide,
          position: parseInt(updatePosition)
        },
        "POST",
        false
      );

      if (response?.status === respEnum.Success) {
        toast.success("Player updated successfully!");
        setUpdatePlayerModalOpen(false);
        setSelectedPlayerForUpdate(null);
        getMatchDetails();
      } else {
        toast.error(response?.message || "Failed to update player");
      }
    } catch (error) {
      console.error("Error updating player:", error);
      toast.error("An error occurred while updating player");
    }
  };

  const calculateWinner = () => {
    const setsA = sideASets.map(s => s === "" ? 0 : parseInt(s) || 0);
    const setsB = sideBSets.map(s => s === "" ? 0 : parseInt(s) || 0);

    let winsA = 0;
    let winsB = 0;

    for (let i = 0; i < 3; i++) {
      if (setsA[i] > setsB[i]) winsA++;
      else if (setsB[i] > setsA[i]) winsB++;
    }

    if (winsA > winsB) setWinner("A");
    else if (winsB > winsA) setWinner("B");
    else setWinner(null);
  };

  const handleSetChange = (side, setIndex, value) => {
    const numValue = value === "" ? "" : value;

    if (side === "A") {
      const newSets = [...sideASets];
      newSets[setIndex] = numValue;
      setSideASets(newSets);
    } else {
      const newSets = [...sideBSets];
      newSets[setIndex] = numValue;
      setSideBSets(newSets);
    }

    setTimeout(() => calculateWinner(), 0);
  };

  const handleUploadResult = async () => {
    if (!winner) {
      toast.error("Please enter complete match results");
      return;
    }

    // Build result logs
    const resultLogs = [];
    const sideAPlayers = match?.players?.filter(p => p.side === "A" && p.is_approved) || [];
    const sideBPlayers = match?.players?.filter(p => p.side === "B" && p.is_approved) || [];

    // Add Side A logs
    sideAPlayers.forEach((player) => {
      for (let i = 0; i < 3; i++) {
        const setScore = sideASets[i] === "" ? 0 : parseInt(sideASets[i]) || 0;
        const opponentScore = sideBSets[i] === "" ? 0 : parseInt(sideBSets[i]) || 0;
        resultLogs.push({
          playerId: player.player_id,
          side: "A",
          round: i + 1,
          points: setScore,
          gameType: 1,
          resultType: setScore > opponentScore ? 1 : setScore < opponentScore ? 2 : 3
        });
      }
    });

    // Add Side B logs
    sideBPlayers.forEach((player) => {
      for (let i = 0; i < 3; i++) {
        const setScore = sideBSets[i] === "" ? 0 : parseInt(sideBSets[i]) || 0;
        const opponentScore = sideASets[i] === "" ? 0 : parseInt(sideASets[i]) || 0;
        resultLogs.push({
          playerId: player.player_id,
          side: "B",
          round: i + 1,
          points: setScore,
          gameType: 1,
          resultType: setScore > opponentScore ? 1 : setScore < opponentScore ? 2 : 3
        });
      }
    });

    try {
      const response = await Common.ApiService.getInstance().request(
        "UploadOpenMatchResultAdmin",
        {
          openMatchId: matchId,
          side: winner,
          resultLogs: resultLogs
        },
        "POST",
        false
      );

      if (response?.status === respEnum.Success) {
        toast.success("Result uploaded successfully!");
        setResultUploadModalOpen(false);
        setSideASets(["", "", ""]);
        setSideBSets(["", "", ""]);
        setWinner(null);
        getMatchDetails();
      } else {
        toast.error(response?.message || "Failed to upload result");
      }
    } catch (error) {
      console.error("Error uploading result:", error);
      toast.error("An error occurred while uploading result");
    }
  };

  useEffect(() => {
    calculateWinner();
  }, [sideASets, sideBSets]);

  const handlePhoneLookup = useCallback(async () => {
    if (!phone || phone.length < 10) {
      setPlayerData(null);
      return;
    }

    const user = await lookupUserByPhone(phoneCode, phone);
    if (user) {
      setPlayerData({
        id: user.id,
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        image: user.image || null,
        level: user.level || null,
        phoneCode: user.phoneCode || phoneCode,
        phone: user.phone || phone,
      });
      toast.success("Player found!");
    } else {
      setPlayerData(null);
      toast.error("Player not found in PlayPro");
    }
  }, [phone, phoneCode, lookupUserByPhone]);

  // Add Player Modal Content
  const AddPlayerModalContent = useCallback(() => {
    return (
      <div className="space-y-4">
        {/* Side and Position Info */}
        {selectedSlot && selectedSlot.side && selectedSlot.position && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-sm text-gray-700">
              Adding player to <span className="font-semibold text-blue-700">Side {selectedSlot.side}, Position {selectedSlot.position}</span>
            </p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#0C1B3A' }}>
              Code
            </label>
            <select
              value={phoneCode}
              onChange={(e) => {
                setPhoneCode(e.target.value);
                setPlayerData(null);
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:outline-none transition-all"
              style={{
                borderColor: '#d1d5db',
                color: '#0C1B3A'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2E55B9';
                e.target.style.boxShadow = '0 0 0 2px rgba(46, 85, 185, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            >
              {phoneCodes.map((code) => (
                <option key={code.value} value={code.value}>
                  {code.label.split(" ")[0]}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#0C1B3A' }}>
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                ref={phoneInputRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const cursorPosition = e.target.selectionStart;
                  setPhone(value);
                  setPlayerData(null);
                  // Maintain cursor position and focus after state update
                  setTimeout(() => {
                    if (phoneInputRef.current) {
                      phoneInputRef.current.focus();
                      const newPosition = Math.min(cursorPosition, value.length);
                      phoneInputRef.current.setSelectionRange(newPosition, newPosition);
                    }
                  }, 0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && phone && phone.length >= 10) {
                    e.preventDefault();
                    handlePhoneLookup();
                  }
                }}
                placeholder="Enter phone number"
                className="w-full px-3 py-2 pr-10 border rounded-lg text-sm focus:outline-none transition-all"
                style={{
                  borderColor: '#d1d5db',
                  color: '#0C1B3A'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2E55B9';
                  e.target.style.boxShadow = '0 0 0 2px rgba(46, 85, 185, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                autoComplete="tel"
              />
              <button
                type="button"
                onClick={handlePhoneLookup}
                disabled={!phone || phone.length < 10 || lookupLoading}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50"
                style={{ color: '#2E55B9' }}
                title="Search Player"
              >
                {lookupLoading ? (
                  <Icon icon="eos-icons:loading" className="w-5 h-5 animate-spin" />
                ) : (
                  <Icon icon="heroicons:magnifying-glass" className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Player Details */}
        {playerData && (
          <div className="p-4 rounded-xl border" style={{ backgroundColor: 'rgba(46, 85, 185, 0.05)', borderColor: '#2E55B9' }}>
            <div className="flex items-center gap-4">
              {playerData.image ? (
                <img
                  src={Common.Utility.getImagePath(playerData.image)}
                  alt={`${playerData.firstName} ${playerData.lastName}`}
                  className="w-16 h-16 object-cover rounded-full border-2"
                  style={{ borderColor: '#2E55B9' }}
                />
              ) : (
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0C1B3A 0%, #2E55B9 100%)' }}>
                  <Icon icon="heroicons:user" className="w-8 h-8 text-white" />
                </div>
              )}
              <div className="flex-1">
                <div className="font-semibold text-lg" style={{ color: '#0C1B3A' }}>
                  {playerData.firstName} {playerData.lastName}
                </div>
                <div className="text-sm mt-1" style={{ color: '#6b7280' }}>
                  {playerData.phoneCode} {playerData.phone}
                </div>
                {playerData.level !== null && (
                  <div className="mt-2">
                    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                      Level: {playerData.level?.toFixed(2) || "N/A"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [selectedSlot, phoneCode, phone, playerData, lookupLoading, phoneCodes, match, handlePhoneLookup]);

  // Add Player Modal Footer
  const AddPlayerModalFooter = useCallback(() => (
    <div className="flex gap-3 w-full">
      <Button
        type="button"
        onClick={() => {
          setAddPlayerModalOpen(false);
          setPlayerData(null);
          setPhone("");
          setSelectedSlot(null);
        }}
        className="flex-1"
        variant="outline"
      >
        Cancel
      </Button>
      <Button
        type="button"
        onClick={handleAddPlayer}
        disabled={!playerData || !selectedSlot?.side || !selectedSlot?.position}
        className="flex-1 text-white"
        style={{
          backgroundColor: (playerData && selectedSlot?.side && selectedSlot?.position) ? '#2E55B9' : '#9ca3af',
          border: 'none'
        }}
      >
        Add Player
      </Button>
    </div>
  ), [playerData, selectedSlot, handleAddPlayer]);

  // Result Upload Modal Content
  const ResultUploadModalContent = useCallback(() => {
    return (
      <div className="space-y-6">
        {/* Section Header */}
        <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Icon icon="heroicons:trophy" className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Match Result</h3>
                <p className="text-amber-100 text-sm">Enter scores for each set</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <div className="font-semibold text-lg w-1/2">Teams</div>
            <div className="flex justify-between w-1/2">
              <div className="text-center font-semibold">Set 1</div>
              <div className="text-center font-semibold">Set 2</div>
              <div className="text-center font-semibold">Set 3</div>
            </div>
          </div>

          {/* Side A */}
          <div className="flex justify-between items-center mb-4">
            <div className="w-1/2 pr-4">
              <div className="font-medium text-gray-900">
                Side A {match?.players && (match.players || []).filter(p => p.side === "A" && p.is_approved).length > 0 && `(${(match.players || []).filter(p => p.side === "A" && p.is_approved).map(p => p.playerName).join(", ")})`}
              </div>
            </div>
            <div className="flex justify-between w-1/2 space-x-2">
              {[0, 1, 2].map((index) => (
                <input
                  key={`sideA-set-${index}`}
                  type="number"
                  min="0"
                  className="w-16 px-2 py-2 text-center bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all outline-none"
                  value={sideASets[index]}
                  onChange={(e) => handleSetChange("A", index, e.target.value)}
                  placeholder="0"
                />
              ))}
            </div>
          </div>

          {/* Side B */}
          <div className="flex justify-between items-center">
            <div className="w-1/2 pr-4">
              <div className="font-medium text-gray-900">
                Side B {match?.players && (match.players || []).filter(p => p.side === "B" && p.is_approved).length > 0 && `(${(match.players || []).filter(p => p.side === "B" && p.is_approved).map(p => p.playerName).join(", ")})`}
              </div>
            </div>
            <div className="flex justify-between w-1/2 space-x-2">
              {[0, 1, 2].map((index) => (
                <input
                  key={`sideB-set-${index}`}
                  type="number"
                  min="0"
                  className="w-16 px-2 py-2 text-center bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all outline-none"
                  value={sideBSets[index]}
                  onChange={(e) => handleSetChange("B", index, e.target.value)}
                  placeholder="0"
                />
              ))}
            </div>
          </div>

          {winner && (
            <div className="mt-4 text-center font-semibold text-green-600">
              Winner: Side {winner}
            </div>
          )}

          {!winner && (sideASets.some(set => set !== "") || sideBSets.some(set => set !== "")) && (
            <div className="mt-4 text-center font-semibold text-gray-500">
              No Result (Tie or Incomplete)
            </div>
          )}
        </div>
      </div>
    );
  }, [match?.players, sideASets, sideBSets, winner, handleSetChange]);

  // Result Upload Modal Footer
  const ResultUploadModalFooter = useCallback(() => (
    <div className="flex gap-3 w-full">
      <Button
        type="button"
        onClick={() => {
          setResultUploadModalOpen(false);
          setSideASets(["", "", ""]);
          setSideBSets(["", "", ""]);
          setWinner(null);
        }}
        className="flex-1"
        variant="outline"
      >
        Cancel
      </Button>
      <Button
        type="button"
        onClick={handleUploadResult}
        disabled={!winner}
        className="flex-1 text-white"
        style={{
          backgroundColor: winner ? '#2E55B9' : '#9ca3af',
          border: 'none'
        }}
      >
        Upload Result
      </Button>
    </div>
  ), [winner, handleUploadResult]);

  const getStatusLabel = (statusValue) => {
    const status = Number(statusValue);
    const statusMap = {
      [OpenMatchStatusEnum.Pending]: {
        label: "Pending",
        class: "bg-yellow-100 text-yellow-800",
        icon: "heroicons:clock"
      },
      [OpenMatchStatusEnum.Completed]: {
        label: "Completed",
        class: "bg-green-100 text-green-800",
        icon: "heroicons:check-circle"
      },
      [OpenMatchStatusEnum.Cancelled]: {
        label: "Cancelled",
        class: "bg-red-100 text-red-800",
        icon: "heroicons:x-circle"
      },
    };
    return statusMap[status] || {
      label: "Unknown",
      class: "bg-gray-100 text-gray-800",
      icon: "heroicons:question-mark-circle"
    };
  };

  const getTypeLabel = (typeValue) => {
    const type = Number(typeValue);
    const typeMap = {
      [OpenMatchTypeEnum.Open]: {
        label: "Open",
        class: "bg-blue-100 text-blue-800"
      },
      [OpenMatchTypeEnum.Exclusive]: {
        label: "Exclusive",
        class: "bg-purple-100 text-purple-800"
      },
    };
    return typeMap[type] || {
      label: "Unknown",
      class: "bg-gray-100 text-gray-800"
    };
  };

  const getActionTypeLabel = (actionType) => {
    const actionMap = {
      1: { label: "Match Created", icon: "heroicons:plus-circle", color: "text-blue-600" },
      2: { label: "Player Joined", icon: "heroicons:user-plus", color: "text-green-600" },
      3: { label: "Player Removed", icon: "heroicons:user-minus", color: "text-red-600" },
      4: { label: "Player Added", icon: "heroicons:user-plus", color: "text-green-600" },
      5: { label: "Player Approved", icon: "heroicons:check-circle", color: "text-green-600" },
      6: { label: "Player Rejected", icon: "heroicons:x-circle", color: "text-red-600" },
      7: { label: "Result Uploaded", icon: "heroicons:trophy", color: "text-yellow-600" },
      8: { label: "Result Approved", icon: "heroicons:check-badge", color: "text-green-600" },
      9: { label: "Status Changed", icon: "heroicons:arrow-path", color: "text-blue-600" },
      10: { label: "Level Changed", icon: "heroicons:chart-bar", color: "text-purple-600" },
    };
    return actionMap[actionType] || { label: "Unknown Action", icon: "heroicons:question-mark-circle", color: "text-gray-600" };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Icon icon="eos-icons:loading" className="w-12 h-12 text-[#0C1B3A] animate-spin mx-auto mb-4" />
          <div className="text-gray-500 text-lg">Loading match details...</div>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Icon icon="heroicons:exclamation-triangle" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <div className="text-gray-500 text-xl">Match not found</div>
          <Button
            onClick={() => navigate("/app/openmatches/manager")}
            className="mt-4 bg-[#0C1B3A] hover:bg-[#1a2a4e]"
          >
            Back to Matches
          </Button>
        </div>
      </div>
    );
  }

  const status = getStatusLabel(match.status);
  const type = getTypeLabel(match.type);

  // Group players by side
  const sideAPlayers = (match.players || []).filter(p => p.side === "A").sort((a, b) => a.position - b.position);
  const sideBPlayers = (match.players || []).filter(p => p.side === "B").sort((a, b) => a.position - b.position);

  return (
    <div className="container mx-auto p-4 space-y-6">
      <Breadcrumbs>
        <BreadcrumbItem onClick={() => navigate("/app/openmatches/manager")}>
          Open Matches
        </BreadcrumbItem>
        <BreadcrumbItem>Match Details</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header Card */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-[#0C1B3A] via-[#1a2a4e] to-[#2E55B9] text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-lg">
                <Icon icon="heroicons:calendar-days" className="w-8 h-8" />
              </div>
              <div>
                <CardTitle className="text-2xl mb-1">Match #{match.id}</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-blue-100">{match.clubName}</span>
                  <span className="text-blue-200">•</span>
                  <span className="text-blue-100">{match.sportName}</span>
                  {match.categoryName && (
                    <>
                      <span className="text-blue-200">•</span>
                      <span className="px-2 py-0.5 bg-white/20 rounded-full text-blue-100 text-xs font-medium">
                        {match.categoryName}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${type.class}`}>
                {type.label}
              </span>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${status.class}`}>
                <Icon icon={status.icon} className="w-5 h-5" />
                {status.label}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-50 p-3 rounded-lg">
                <Icon icon="heroicons:calendar" className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-semibold text-[#0C1B3A]">
                  {moment(match.start_datetime).format("DD MMM YYYY")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-green-50 p-3 rounded-lg">
                <Icon icon="heroicons:clock" className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Time</p>
                <p className="font-semibold text-[#0C1B3A]">
                  {moment(match.start_datetime).format("HH:mm")} - {moment(match.end_datetime).format("HH:mm")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-purple-50 p-3 rounded-lg">
                <Icon icon="heroicons:currency-dollar" className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Court Charges</p>
                <p className="font-semibold text-green-600">
                  Rs. {match.court_charges?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-orange-50 p-3 rounded-lg">
                <Icon icon="heroicons:star" className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Required Level</p>
                <p className="font-semibold text-[#0C1B3A]">
                  {match.required_level || "N/A"}
                </p>
              </div>
            </div>
            {match.categoryName && (
              <div className="flex items-center gap-3">
                <div className="bg-indigo-50 p-3 rounded-lg">
                  <Icon icon="heroicons:tag" className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <p className="font-semibold text-[#0C1B3A]">
                    {match.categoryName}
                  </p>
                </div>
              </div>
            )}
          </div>

          {match.clubAddress && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-start gap-3">
                <Icon icon="heroicons:map-pin" className="w-5 h-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Club Address</p>
                  <p className="text-gray-700">{match.clubAddress}</p>
                </div>
              </div>
            </div>
          )}

          {match.notes && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-start gap-3">
                <Icon icon="heroicons:document-text" className="w-5 h-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Notes</p>
                  <p className="text-gray-700">{match.notes}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Players Battle Arena */}
          <Card className="border-0 shadow-2xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-[#0C1B3A] via-[#1a2a4e] to-[#0C1B3A] text-white py-6">
              <CardTitle className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-2">
                  <Icon icon="heroicons:users" className="w-7 h-7" />
                  <span className="text-xl">Match Arena</span>
                </div>
                <div className="px-4 py-1 bg-white/20 rounded-full text-sm">
                  {match.currentPlayerCount || 0}/{match.playerCount || 0} Players
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Battle Arena Layout */}
              <div className="relative bg-gradient-to-b from-gray-50 to-white min-h-[400px]">
                {/* Court Pattern Background */}
                <div className="absolute inset-0 opacity-5">
                  <div className="h-full w-full" style={{
                    backgroundImage: `repeating-linear-gradient(90deg, #0C1B3A 0px, #0C1B3A 2px, transparent 2px, transparent 50px)`,
                  }}></div>
                </div>

                <div className="relative flex flex-col lg:flex-row">
                  {/* Side A - Left */}
                  <div className="flex-1 p-6 bg-gradient-to-br from-blue-50/80 via-blue-50/50 to-transparent">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full shadow-lg shadow-blue-500/50"></div>
                        <h3 className="font-bold text-xl text-[#0C1B3A]">Side A</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-full shadow-md">
                          {sideAPlayers.length}/{match.playerCount / 2}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {sideAPlayers.map((player, idx) => (
                        <div
                          key={player.id}
                          className="group relative flex items-center gap-4 p-4 bg-white rounded-2xl border-2 border-blue-100 hover:border-blue-400 shadow-lg hover:shadow-xl hover:shadow-blue-500/20 transition-all duration-300 transform hover:-translate-y-1"
                        >
                          <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                          <div className="relative">
                            {player.playerImage ? (
                              <img
                                src={Common.Utility.getImagePath(player.playerImage)}
                                alt={player.playerName}
                                className="w-14 h-14 rounded-xl object-cover border-3 border-blue-200 shadow-md"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-md">
                                <Icon icon="heroicons:user" className="w-7 h-7 text-blue-600" />
                              </div>
                            )}
                            {player.is_creator && (
                              <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center shadow-sm">
                                <Icon icon="heroicons:star" className="w-3 h-3 text-yellow-900" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[#0C1B3A] truncate">{player.playerName}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                                #{player.position}
                              </span>
                              {player.is_creator && (
                                <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                                  Creator
                                </span>
                              )}
                              {/* Current Level Display - Must show after result upload */}
                              {(match.is_result_uploaded || (player.currentLevel !== null && player.currentLevel !== undefined)) && (
                                <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                                  Current: {player.currentLevel !== null && player.currentLevel !== undefined ? player.currentLevel.toFixed(2) : "N/A"}
                                </span>
                              )}
                              {/* Level Display - Must show both before and after if result uploaded */}
                              {match.is_result_uploaded && (
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                    Before: {player.before_played_level !== null && player.before_played_level !== undefined ? player.before_played_level.toFixed(2) : "N/A"}
                                  </span>
                                  {player.after_played_level !== null && player.after_played_level !== undefined ? (
                                    <>
                                      <Icon
                                        icon={player.after_played_level > (player.before_played_level || 0) ? "heroicons:arrow-trending-up" : "heroicons:arrow-trending-down"}
                                        className={`w-4 h-4 ${player.after_played_level > (player.before_played_level || 0) ? 'text-green-600' : 'text-red-600'
                                          }`}
                                      />
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${player.after_played_level > (player.before_played_level || 0)
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                        }`}>
                                        After: {player.after_played_level.toFixed(2)}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                      After: N/A
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Show before level only if result not uploaded and level exists */}
                              {!match.is_result_uploaded && player.before_played_level !== null && player.before_played_level !== undefined && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                  Before: {player.before_played_level.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${player.is_approved
                              ? 'bg-green-100'
                              : 'bg-yellow-100 animate-pulse'
                              }`}>
                              <Icon
                                icon={player.is_approved ? "heroicons:check-circle-solid" : "heroicons:clock"}
                                className={`w-6 h-6 ${player.is_approved ? 'text-green-600' : 'text-yellow-600'}`}
                              />
                            </div>
                            {/* Remove Button - Only show if match not finalized */}
                            {match.status !== OpenMatchStatusEnum.Completed || !match.is_result_uploaded ? (
                              <button
                                onClick={() => handleRemovePlayer(player.player_id)}
                                className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors"
                                title="Remove Player"
                              >
                                <Icon icon="heroicons:trash" className="w-4 h-4 text-red-600" />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      {Array.from({ length: Math.max(0, match.playerCount / 2 - sideAPlayers.length) }).map((_, i) => {
                        const position = sideAPlayers.length + i + 1;
                        return (
                          <div key={`empty-a-${i}`} className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-50/50 to-transparent rounded-2xl border-2 border-dashed border-blue-200 hover:border-blue-400 transition-colors">
                            <button
                              onClick={() => {
                                setSelectedSlot({ side: "A", position });
                                setAddPlayerModalOpen(true);
                                setPhone("");
                                setPlayerData(null);
                              }}
                              className="text-center w-full"
                              disabled={match.status === OpenMatchStatusEnum.Completed && match.is_result_uploaded}
                            >
                              <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-blue-100/50 flex items-center justify-center hover:bg-blue-200/50 transition-colors">
                                <Icon icon="heroicons:user-plus" className="w-6 h-6 text-blue-400" />
                              </div>
                              <p className="text-sm text-blue-400 font-medium">Add Player #{position}</p>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* VS Center Divider */}
                  <div className="hidden lg:flex flex-col items-center justify-center py-8 px-6 relative shrink-0">
                    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] rounded-full blur-xl opacity-50 animate-pulse"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-br from-[#0C1B3A] via-[#1a2a4e] to-[#2E55B9] rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                        <span className="text-white font-black text-2xl tracking-wider">VS</span>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Battle</p>
                    </div>
                  </div>

                  {/* Mobile VS */}
                  <div className="lg:hidden flex items-center justify-center py-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">VS</span>
                    </div>
                  </div>

                  {/* Side B - Right */}
                  <div className="flex-1 p-6 bg-gradient-to-bl from-purple-50/80 via-purple-50/50 to-transparent">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full shadow-lg shadow-purple-500/50"></div>
                        <h3 className="font-bold text-xl text-[#0C1B3A]">Side B</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-purple-600 text-white text-sm font-semibold rounded-full shadow-md">
                          {sideBPlayers.length}/{match.playerCount / 2}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {sideBPlayers.map((player, idx) => (
                        <div
                          key={player.id}
                          className="group relative flex items-center gap-4 p-4 bg-white rounded-2xl border-2 border-purple-100 hover:border-purple-400 shadow-lg hover:shadow-xl hover:shadow-purple-500/20 transition-all duration-300 transform hover:-translate-y-1"
                        >
                          <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                          <div className="relative">
                            {player.playerImage ? (
                              <img
                                src={Common.Utility.getImagePath(player.playerImage)}
                                alt={player.playerName}
                                className="w-14 h-14 rounded-xl object-cover border-3 border-purple-200 shadow-md"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center shadow-md">
                                <Icon icon="heroicons:user" className="w-7 h-7 text-purple-600" />
                              </div>
                            )}
                            {player.is_creator && (
                              <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center shadow-sm">
                                <Icon icon="heroicons:star" className="w-3 h-3 text-yellow-900" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[#0C1B3A] truncate">{player.playerName}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                                #{player.position}
                              </span>
                              {player.is_creator && (
                                <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                                  Creator
                                </span>
                              )}
                              {/* Current Level Display - Must show after result upload */}
                              {(match.is_result_uploaded || (player.currentLevel !== null && player.currentLevel !== undefined)) && (
                                <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                                  Current: {player.currentLevel !== null && player.currentLevel !== undefined ? player.currentLevel.toFixed(2) : "N/A"}
                                </span>
                              )}
                              {/* Level Display - Must show both before and after if result uploaded */}
                              {match.is_result_uploaded && (
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                    Before: {player.before_played_level !== null && player.before_played_level !== undefined ? player.before_played_level.toFixed(2) : "N/A"}
                                  </span>
                                  {player.after_played_level !== null && player.after_played_level !== undefined ? (
                                    <>
                                      <Icon
                                        icon={player.after_played_level > (player.before_played_level || 0) ? "heroicons:arrow-trending-up" : "heroicons:arrow-trending-down"}
                                        className={`w-4 h-4 ${player.after_played_level > (player.before_played_level || 0) ? 'text-green-600' : 'text-red-600'
                                          }`}
                                      />
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${player.after_played_level > (player.before_played_level || 0)
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                        }`}>
                                        After: {player.after_played_level.toFixed(2)}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                      After: N/A
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Show before level only if result not uploaded and level exists */}
                              {!match.is_result_uploaded && player.before_played_level !== null && player.before_played_level !== undefined && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                  Before: {player.before_played_level.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${player.is_approved
                              ? 'bg-green-100'
                              : 'bg-yellow-100 animate-pulse'
                              }`}>
                              <Icon
                                icon={player.is_approved ? "heroicons:check-circle-solid" : "heroicons:clock"}
                                className={`w-6 h-6 ${player.is_approved ? 'text-green-600' : 'text-yellow-600'}`}
                              />
                            </div>
                            {/* Remove Button - Only show if match not finalized */}
                            {match.status !== OpenMatchStatusEnum.Completed || !match.is_result_uploaded ? (
                              <button
                                onClick={() => handleRemovePlayer(player.player_id)}
                                className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors"
                                title="Remove Player"
                              >
                                <Icon icon="heroicons:trash" className="w-4 h-4 text-red-600" />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      {Array.from({ length: Math.max(0, match.playerCount / 2 - sideBPlayers.length) }).map((_, i) => {
                        const position = sideBPlayers.length + i + 1;
                        return (
                          <div key={`empty-b-${i}`} className="flex items-center justify-center p-6 bg-gradient-to-l from-purple-50/50 to-transparent rounded-2xl border-2 border-dashed border-purple-200 hover:border-purple-400 transition-colors">
                            <button
                              onClick={() => {
                                setSelectedSlot({ side: "B", position });
                                setAddPlayerModalOpen(true);
                                setPhone("");
                                setPlayerData(null);
                              }}
                              className="text-center w-full"
                              disabled={match.status === OpenMatchStatusEnum.Completed && match.is_result_uploaded}
                            >
                              <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-purple-100/50 flex items-center justify-center hover:bg-purple-200/50 transition-colors">
                                <Icon icon="heroicons:user-plus" className="w-6 h-6 text-purple-400" />
                              </div>
                              <p className="text-sm text-purple-400 font-medium">Add Player #{position}</p>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Result Upload Section - Show if Exclusive type or Admin and result not uploaded */}
          {((match.type === OpenMatchTypeEnum.Exclusive) || (Common.getUser()?.roleId === RoleEnum.Super_Admin)) && !match.is_result_uploaded && (
            <Card className="border-0 shadow-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 text-white py-5">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Icon icon="heroicons:trophy" className="w-6 h-6" />
                    </div>
                    <span className="text-xl font-bold">Upload Match Result</span>
                  </div>
                  <Button
                    onClick={() => setResultUploadModalOpen(true)}
                    disabled={!match.players || match.players.length < match.playerCount}
                    className={`${(!match.players || match.players.length < match.playerCount) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-white text-amber-600 hover:bg-amber-50'}`}
                    title={(!match.players || match.players.length < match.playerCount) ? `Need ${match.playerCount} players to upload result. Currently have ${match.players?.length || 0} players.` : ''}
                  >
                    <Icon icon="heroicons:plus-circle" className="w-5 h-5 mr-2" />
                    Upload Result
                  </Button>
                </CardTitle>
              </CardHeader>
            </Card>
          )}

          {/* Results Section */}
          {match.is_result_uploaded && match.results && match.results.length > 0 && (
            <Card className="border-0 shadow-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 text-white py-5">
                <CardTitle className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Icon icon="heroicons:trophy" className="w-6 h-6" />
                  </div>
                  <span className="text-xl font-bold">Match Results</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 bg-gradient-to-b from-amber-50 to-white">
                {/* Results Table - Same format as upload */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden mb-6">
                  <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 p-4">
                    <h3 className="text-white font-bold text-lg text-center">Match Score</h3>
                  </div>
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <div className="font-semibold text-lg w-1/2">Teams</div>
                      <div className="flex justify-between w-1/2">
                        <div className="text-center font-semibold">Set 1</div>
                        <div className="text-center font-semibold">Set 2</div>
                        <div className="text-center font-semibold">Set 3</div>
                      </div>
                    </div>

                    {match.results.map((result, idx) => {
                      const sideColor = result.side === 'A' ? 'blue' : 'purple';
                      const isWinner = match.results.length === 2 &&
                        result.totalPoints === Math.max(...match.results.map(r => r.totalPoints));

                      // Get sets from result - get points for each round
                      let set1 = 0, set2 = 0, set3 = 0;
                      if (result.sets && Array.isArray(result.sets) && result.sets.length > 0) {
                        // Handle both number and string round values
                        const set1Data = result.sets.find(s => Number(s.round) === 1);
                        const set2Data = result.sets.find(s => Number(s.round) === 2);
                        const set3Data = result.sets.find(s => Number(s.round) === 3);
                        set1 = set1Data?.points ?? 0;
                        set2 = set2Data?.points ?? 0;
                        set3 = set3Data?.points ?? 0;
                      }

                      return (
                        <div key={result.side} className={`flex justify-between items-center mb-4 p-4 rounded-xl ${sideColor === 'blue' ? 'bg-blue-50' : 'bg-purple-50'} ${isWinner ? 'ring-2 ring-yellow-400' : ''}`}>
                          <div className="w-1/2 pr-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white ${sideColor === 'blue' ? 'bg-blue-600' : 'bg-purple-600'}`}>
                                {result.side}
                              </div>
                              <div className="font-medium text-gray-900">
                                Side {result.side}
                                {isWinner && (
                                  <span className="ml-2 px-2 py-0.5 bg-yellow-400 text-yellow-900 rounded-full text-xs font-bold">
                                    WINNER
                                  </span>
                                )}
                              </div>
                            </div>
                            {(result.players || []).length > 0 && (
                              <div className="text-xs text-gray-600 mt-1">
                                ({[...new Set((result.players || []).map(p => p.playerName))].join(", ")})
                              </div>
                            )}
                          </div>
                          <div className="flex justify-between w-1/2 space-x-2">
                            <div className={`w-16 px-2 py-2 text-center rounded-lg font-bold text-lg ${sideColor === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                              {set1}
                            </div>
                            <div className={`w-16 px-2 py-2 text-center rounded-lg font-bold text-lg ${sideColor === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                              {set2}
                            </div>
                            <div className={`w-16 px-2 py-2 text-center rounded-lg font-bold text-lg ${sideColor === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                              {set3}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Total Score Row */}
                    <div className="flex justify-between items-center pt-4 border-t-2 border-gray-200">
                      <div className="font-bold text-lg w-1/2">Total</div>
                      <div className="flex justify-between w-1/2 space-x-2">
                        {match.results.map((result) => {
                          const sideColor = result.side === 'A' ? 'blue' : 'purple';
                          return (
                            <div key={result.side} className={`w-16 px-2 py-2 text-center rounded-lg font-bold text-lg ${sideColor === 'blue' ? 'bg-blue-600 text-white' : 'bg-purple-600 text-white'}`}>
                              {result.totalPoints}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Activity Logs Section */}
          {match.logs && match.logs.length > 0 && (
            <Card className="border-0 shadow-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-700 text-white py-5">
                <CardTitle className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Icon icon="heroicons:clipboard-document-list" className="w-5 h-5" />
                  </div>
                  <span className="text-xl font-bold">Activity Timeline</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                    {match.logs.length} events
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 bg-gradient-to-b from-slate-50 to-white">
                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-slate-300 via-slate-200 to-transparent"></div>

                  <div className="space-y-4">
                    {match.logs.map((log, index) => {
                      const actionInfo = getActionTypeLabel(log.action_type);
                      const bgColorClass = actionInfo.color.replace('text-', 'bg-').replace('-600', '-100');

                      return (
                        <div
                          key={log.id}
                          className="relative pl-14 group"
                        >
                          {/* Timeline Dot */}
                          <div className={`absolute left-4 top-4 w-5 h-5 rounded-full border-4 border-white shadow-md ${bgColorClass} group-hover:scale-125 transition-transform`}>
                          </div>

                          <div className={`p-4 bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-lg transition-all duration-300 group-hover:border-gray-200`}>
                            <div className="flex items-start gap-4">
                              <div className={`shrink-0 w-12 h-12 rounded-xl ${bgColorClass} flex items-center justify-center shadow-sm`}>
                                <Icon icon={actionInfo.icon} className={`w-6 h-6 ${actionInfo.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div>
                                    <p className="font-bold text-[#0C1B3A]">{actionInfo.label}</p>
                                    {log.details && (
                                      <p className="text-sm text-gray-600 mt-1">{log.details}</p>
                                    )}
                                  </div>
                                </div>
                                {log.playerName && (
                                  <div className="flex items-center gap-2 mt-3 p-2 bg-gray-50 rounded-lg">
                                    {log.playerImage ? (
                                      <img
                                        src={Common.Utility.getImagePath(log.playerImage)}
                                        alt={log.playerName}
                                        className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm"
                                      />
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                        <Icon icon="heroicons:user" className="w-4 h-4 text-gray-500" />
                                      </div>
                                    )}
                                    <span className="text-sm font-medium text-gray-700">{log.playerName}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Creator Info */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-br from-[#0C1B3A] via-[#1a2a4e] to-[#2E55B9] p-6">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  {match.creatorImage ? (
                    <img
                      src={Common.Utility.getImagePath(match.creatorImage)}
                      alt={match.creatorName}
                      className="w-20 h-20 rounded-2xl object-cover border-4 border-white/30 shadow-xl"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border-4 border-white/30">
                      <Icon icon="heroicons:user" className="w-10 h-10 text-white" />
                    </div>
                  )}
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                    <Icon icon="heroicons:star" className="w-4 h-4 text-yellow-900" />
                  </div>
                </div>
                <p className="font-bold text-xl text-white mb-1">{match.creatorName}</p>
                <div className="flex items-center gap-2 text-blue-200 text-sm">
                  <Icon icon="heroicons:trophy" className="w-4 h-4" />
                  <span>Match Organizer</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Match Stats */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] text-white">
              <CardTitle className="flex items-center gap-2">
                <Icon icon="heroicons:chart-bar" className="w-5 h-5" />
                Match Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-3">
              {/* Progress Bar for Players */}
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Total Players</span>
                  <span className="font-bold text-[#0C1B3A]">
                    {match.currentPlayerCount || 0}/{match.playerCount || 0}
                  </span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
                    style={{ width: `${((match.currentPlayerCount || 0) / (match.playerCount || 4)) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Side Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white text-center shadow-lg shadow-blue-500/30">
                  <Icon icon="heroicons:users" className="w-6 h-6 mx-auto mb-1 opacity-80" />
                  <p className="text-2xl font-bold">{match.playersOnSideA || 0}</p>
                  <p className="text-xs opacity-80">Side A</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl text-white text-center shadow-lg shadow-purple-500/30">
                  <Icon icon="heroicons:users" className="w-6 h-6 mx-auto mb-1 opacity-80" />
                  <p className="text-2xl font-bold">{match.playersOnSideB || 0}</p>
                  <p className="text-xs opacity-80">Side B</p>
                </div>
              </div>

              {/* Available Slots */}
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-green-100">
                <p className="text-sm font-medium text-gray-600 mb-3 flex items-center gap-2">
                  <Icon icon="heroicons:user-plus" className="w-4 h-4 text-green-600" />
                  Available Slots
                </p>
                <div className="flex justify-around">
                  <div className="text-center">
                    <p className="text-lg font-bold text-blue-600">{Math.max(0, match.availableSlotsSideA || 0)}</p>
                    <p className="text-xs text-gray-500">Side A</p>
                  </div>
                  <div className="w-px bg-gray-200"></div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-purple-600">{Math.max(0, match.availableSlotsSideB || 0)}</p>
                    <p className="text-xs text-gray-500">Side B</p>
                  </div>
                </div>
              </div>

              {/* Result Status */}
              <div className={`p-4 rounded-xl flex items-center justify-between ${match.is_result_uploaded
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'
                : 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-yellow-200'
                }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${match.is_result_uploaded ? 'bg-green-100' : 'bg-yellow-100'
                    }`}>
                    <Icon
                      icon={match.is_result_uploaded ? "heroicons:trophy" : "heroicons:clock"}
                      className={`w-5 h-5 ${match.is_result_uploaded ? 'text-green-600' : 'text-yellow-600'}`}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Result Status</p>
                    <p className={`font-bold ${match.is_result_uploaded ? 'text-green-700' : 'text-yellow-700'}`}>
                      {match.is_result_uploaded ? "Uploaded" : "Pending"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Approval */}
          {match.pendingApproval && (
            <Card className="border-0 shadow-lg border-l-4 border-yellow-500">
              <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50">
                <CardTitle className="flex items-center gap-2 text-yellow-800">
                  <Icon icon="heroicons:clock" className="w-5 h-5" />
                  Pending Approval
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Uploaded By</p>
                    <p className="font-semibold text-[#0C1B3A]">{match.pendingApproval.uploadedBy}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Side</p>
                    <p className="font-semibold text-[#0C1B3A]">Team {match.pendingApproval.side}</p>
                  </div>
                  {match.pendingApproval.timestamps && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Uploaded On</p>
                      <p className="text-sm text-gray-700">
                        {moment(match.pendingApproval.timestamps).format("DD MMM YYYY, HH:mm")}
                      </p>
                    </div>
                  )}
                  {match.pendingApproval.resultLogs && match.pendingApproval.resultLogs.length > 0 && (
                    <div className="pt-3 border-t">
                      <p className="text-sm font-medium text-gray-700 mb-2">Result Details</p>
                      <div className="space-y-2">
                        {[1, 2, 3].map((round) => {
                          const sideALogs = match.pendingApproval.resultLogs.filter(l => l.side === "A" && l.round === round);
                          const sideBLogs = match.pendingApproval.resultLogs.filter(l => l.side === "B" && l.round === round);
                          const sideAScore = sideALogs.length > 0 ? sideALogs[0].points : 0;
                          const sideBScore = sideBLogs.length > 0 ? sideBLogs[0].points : 0;

                          return (
                            <div key={round} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                              <span className="text-xs font-medium text-gray-600">Set {round}</span>
                              <div className="flex items-center gap-3">
                                <span className={`text-sm font-semibold ${sideAScore > sideBScore ? 'text-blue-600' : 'text-gray-600'}`}>
                                  Side A: {sideAScore}
                                </span>
                                <span className="text-gray-400">vs</span>
                                <span className={`text-sm font-semibold ${sideBScore > sideAScore ? 'text-purple-600' : 'text-gray-600'}`}>
                                  Side B: {sideBScore}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="pt-3 border-t">
                    <p className="text-xs text-gray-500">
                      Waiting for opposing team approval
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Player Modal */}
      <DialogModal
        open={addPlayerModalOpen}
        setOpen={handleAddPlayerModalClose}
        title="Add Player"
        className="max-w-md"
        Child={AddPlayerModalContent}
        Footer={AddPlayerModalFooter}
      />

      {/* Update Player Modal */}
      <DialogModal
        isOpen={updatePlayerModalOpen}
        onClose={() => {
          setUpdatePlayerModalOpen(false);
          setSelectedPlayerForUpdate(null);
          setUpdateSide("");
          setUpdatePosition("");
        }}
        title="Update Player Position"
        size="md"
      >
        <div className="space-y-4">
          {selectedPlayerForUpdate && (
            <div className="p-4 rounded-xl border" style={{ backgroundColor: 'rgba(46, 85, 185, 0.05)', borderColor: '#2E55B9' }}>
              <div className="flex items-center gap-4">
                {selectedPlayerForUpdate.playerImage ? (
                  <img
                    src={Common.Utility.getImagePath(selectedPlayerForUpdate.playerImage)}
                    alt={selectedPlayerForUpdate.playerName}
                    className="w-16 h-16 object-cover rounded-full border-2"
                    style={{ borderColor: '#2E55B9' }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0C1B3A 0%, #2E55B9 100%)' }}>
                    <Icon icon="heroicons:user" className="w-8 h-8 text-white" />
                  </div>
                )}
                <div>
                  <div className="font-semibold text-lg" style={{ color: '#0C1B3A' }}>
                    {selectedPlayerForUpdate.playerName}
                  </div>
                  <div className="text-sm mt-1" style={{ color: '#6b7280' }}>
                    Current: Side {selectedPlayerForUpdate.side}, Position {selectedPlayerForUpdate.position}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0C1B3A' }}>
              Side *
            </label>
            <FormikSelect
              name="updateSide"
              data={[
                { value: "A", label: "Side A" },
                { value: "B", label: "Side B" }
              ]}
              value={updateSide}
              onChange={(value) => setUpdateSide(value)}
              placeholder="Select Side"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0C1B3A' }}>
              Position *
            </label>
            <FormikInput
              type="number"
              min="1"
              max={match?.playerCount / 2 || 2}
              value={updatePosition}
              onChange={(e) => setUpdatePosition(e.target.value)}
              placeholder="Enter Position"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={() => {
                setUpdatePlayerModalOpen(false);
                setSelectedPlayerForUpdate(null);
                setUpdateSide("");
                setUpdatePosition("");
              }}
              className="flex-1"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpdatePlayerSubmit}
              disabled={!updateSide || !updatePosition}
              className="flex-1 text-white"
              style={{
                backgroundColor: (updateSide && updatePosition) ? '#2E55B9' : '#9ca3af',
                border: 'none'
              }}
            >
              Update Player
            </Button>
          </div>
        </div>
      </DialogModal>

      {/* Result Upload Modal */}
      <DialogModal
        open={resultUploadModalOpen}
        setOpen={(value) => {
          setResultUploadModalOpen(value);
          if (!value) {
            setSideASets(["", "", ""]);
            setSideBSets(["", "", ""]);
            setWinner(null);
          }
        }}
        title="Upload Match Result"
        className="max-w-2xl"
        Child={ResultUploadModalContent}
        Footer={ResultUploadModalFooter}
      />
    </div>
  );
};

export default MatchDetails;
